import React, { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronDown, Check, X } from "lucide-react";

/* ========== Input nomor telepon Indonesia (+62) ========== */
const PHONE_MAX_DIGITS = 13;   // nomor nasional tanpa +62, mis. 81234567890
const PHONE_MIN_DIGITS = 8;

/** "+62811-1111-1111", "0811 1111 1111", "62811..." → "8111111111" */
export function toNationalPhoneDigits(value) {
  let digits = String(value ?? "").replace(/\D/g, "");
  if (digits.startsWith("62")) digits = digits.slice(2);
  digits = digits.replace(/^0+/, "");
  return digits.slice(0, PHONE_MAX_DIGITS);
}

/** Nilai yang disimpan ke backend: "+628111111111" (string kosong bila belum diisi). */
export function toPhoneE164(value) {
  const digits = toNationalPhoneDigits(value);
  return digits ? `+62${digits}` : "";
}

export function isValidPhone(value) {
  return toNationalPhoneDigits(value).length >= PHONE_MIN_DIGITS;
}

export function PhoneInput({ value, onChange, required = false, placeholder = "81234567890", testid }) {
  const digits = toNationalPhoneDigits(value);
  return (
    <div className="flex">
      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-slate-300 bg-slate-100 text-sm font-medium text-slate-600 select-none">
        +62
      </span>
      <input
        type="tel"
        inputMode="numeric"
        autoComplete="tel"
        className="kedan-input !rounded-l-none font-mono"
        required={required}
        minLength={PHONE_MIN_DIGITS}
        maxLength={PHONE_MAX_DIGITS}
        value={digits}
        placeholder={placeholder}
        // Karakter selain angka dibuang saat diketik, jadi huruf/simbol tidak pernah masuk.
        onChange={(e) => onChange(toPhoneE164(e.target.value))}
        data-testid={testid}
      />
    </div>
  );
}

/* ========== Pilihan ganda (multi-select) ========== */
export function MultiSelect({
  options = [], value = [], onChange, placeholder = "Pilih satu atau lebih",
  emptyText = "Belum ada pilihan tersedia.", testid,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = Array.isArray(value) ? value : [];
  const shown = options.filter((o) => o.toLowerCase().includes(q.trim().toLowerCase()));

  const toggle = (opt) =>
    onChange(selected.includes(opt) ? selected.filter((v) => v !== opt) : [...selected, opt]);

  return (
    <div className="relative" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="kedan-input flex items-center justify-between gap-2 text-left min-h-[42px]"
      >
        <span className="flex flex-wrap gap-1.5 items-center">
          {selected.length === 0 && <span className="text-slate-400">{placeholder}</span>}
          {selected.map((v) => (
            <span key={v} className="inline-flex items-center gap-1 bg-blue-50 text-[#045498] border border-blue-200 rounded px-1.5 py-0.5 text-xs font-medium">
              {v}
              <X
                className="w-3 h-3 cursor-pointer hover:text-red-600"
                onClick={(e) => { e.stopPropagation(); toggle(v); }}
              />
            </span>
          ))}
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute z-40 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden">
            {options.length > 8 && (
              <div className="p-2 border-b border-slate-100">
                <input
                  autoFocus className="kedan-input !py-1.5 text-sm" placeholder="Cari..."
                  value={q} onChange={(e) => setQ(e.target.value)}
                />
              </div>
            )}
            <div className="max-h-56 overflow-y-auto py-1">
              {options.length === 0 && <div className="px-3 py-3 text-sm text-slate-500">{emptyText}</div>}
              {options.length > 0 && shown.length === 0 && (
                <div className="px-3 py-3 text-sm text-slate-500">Tidak ada yang cocok.</div>
              )}
              {shown.map((opt) => {
                const active = selected.includes(opt);
                return (
                  <button
                    key={opt} type="button" onClick={() => toggle(opt)}
                    className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-slate-50 ${active ? "text-[#045498] font-medium" : "text-slate-700"}`}
                  >
                    <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${active ? "bg-[#045498] border-[#045498]" : "border-slate-300"}`}>
                      {active && <Check className="w-3 h-3 text-white" />}
                    </span>
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ========== Input koordinat peta ========== */
const COORD_MAX = { lat: 90, lng: 180 };

export function isValidCoordinate(value, axis = "lat") {
  if (value === "" || value === null || value === undefined) return false;
  const num = Number(value);
  return Number.isFinite(num) && Math.abs(num) <= COORD_MAX[axis];
}

export function CoordinateInput({ axis = "lat", value, onChange, required = true, testid }) {
  const max = COORD_MAX[axis];
  return (
    <input
      type="number"
      inputMode="decimal"
      step="any"           // koordinat desimal bebas, tanpa pembulatan step
      min={-max}
      max={max}
      className="kedan-input font-mono text-sm"
      required={required}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={axis === "lat" ? "3.5952" : "98.6722"}
      data-testid={testid}
    />
  );
}

export function StatCard({ icon: Icon, label, value, sub, accent = "blue", testid }) {
  const accents = {
    blue: { bg: "bg-blue-50", text: "text-[#045498]" },
    amber: { bg: "bg-amber-50", text: "text-amber-700" },
    emerald: { bg: "bg-emerald-50", text: "text-emerald-700" },
    violet: { bg: "bg-violet-50", text: "text-violet-700" },
    orange: { bg: "bg-orange-50", text: "text-orange-700" },
    rose: { bg: "bg-rose-50", text: "text-rose-700" },
  }[accent] || { bg: "bg-blue-50", text: "text-[#045498]" };

  return (
    <div className="kedan-card p-5" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="kedan-label text-slate-500">{label}</div>
        {Icon && (
          <div className={`w-9 h-9 rounded-md ${accents.bg} ${accents.text} flex items-center justify-center`}>
            <Icon className="w-4 h-4" />
          </div>
        )}
      </div>
      <div className="mt-3 text-2xl font-display font-bold text-slate-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

export function Section({ title, subtitle, action, children }) {
  return (
    <section className="space-y-4">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-display font-semibold text-slate-900">{title}</h2>
          {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

export function EmptyState({ title, description, action, icon: Icon }) {
  return (
    <div className="kedan-card p-12 flex flex-col items-center justify-center text-center" data-testid="empty-state">
      {Icon && <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-4 text-slate-400">
        <Icon className="w-6 h-6" />
      </div>}
      <div className="font-display font-semibold text-slate-800">{title}</div>
      {description && <div className="text-sm text-slate-500 mt-1 max-w-md">{description}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Skeleton({ className = "h-4 w-full" }) {
  return <div className={`skeleton ${className}`} />;
}

// ─── Pagination ──────────────────────────────────────────────
const PER_PAGE_OPTIONS = [10, 25, 50];

export function usePagination(items, defaultPerPage = 10) {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(defaultPerPage);

  const totalPages = Math.max(1, Math.ceil(items.length / perPage));
  const safePage = Math.min(page, totalPages);

  const paged = useMemo(
    () => items.slice((safePage - 1) * perPage, safePage * perPage),
    [items, safePage, perPage]
  );

  const changePerPage = (v) => {
    setPerPage(v);
    setPage(1);
  };

  return { page: safePage, setPage, perPage, changePerPage, totalPages, paged, totalItems: items.length };
}

export function Pagination({ page, totalPages, totalItems, perPage, onPageChange, onPerPageChange }) {
  if (totalItems <= 0) return null;

  const from = (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, totalItems);

  // Build visible page numbers with ellipsis
  const getPageNumbers = () => {
    const pages = [];
    const delta = 1;
    const left = Math.max(2, page - delta);
    const right = Math.min(totalPages - 1, page + delta);

    pages.push(1);
    if (left > 2) pages.push("...");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push("...");
    if (totalPages > 1) pages.push(totalPages);
    return pages;
  };

  return (
    <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between gap-4 flex-wrap bg-white" data-testid="pagination">
      {/* Left: info + per-page */}
      <div className="flex items-center gap-3 text-xs text-slate-500">
        <span>Menampilkan <b className="text-slate-700">{from}–{to}</b> dari <b className="text-slate-700">{totalItems}</b></span>
        <span className="text-slate-300">|</span>
        <div className="flex items-center gap-1.5">
          <span>Baris:</span>
          <select
            className="kedan-input !w-16 !py-1 text-xs"
            value={perPage}
            onChange={(e) => onPerPageChange(Number(e.target.value))}
            data-testid="per-page-select"
          >
            {PER_PAGE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Right: page buttons */}
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(1)}
          disabled={page <= 1}
          className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed text-slate-600"
          title="Halaman pertama"
          data-testid="page-first"
        >
          <ChevronsLeft className="w-4 h-4" />
        </button>
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed text-slate-600"
          title="Sebelumnya"
          data-testid="page-prev"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        {getPageNumbers().map((p, i) =>
          p === "..." ? (
            <span key={`e-${i}`} className="px-1 text-xs text-slate-400">…</span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className={`min-w-[30px] h-[30px] rounded text-xs font-medium transition-colors ${
                p === page
                  ? "bg-[#045498] text-white shadow-sm"
                  : "text-slate-700 hover:bg-slate-100"
              }`}
              data-testid={`page-${p}`}
            >
              {p}
            </button>
          )
        )}

        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed text-slate-600"
          title="Berikutnya"
          data-testid="page-next"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={page >= totalPages}
          className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed text-slate-600"
          title="Halaman terakhir"
          data-testid="page-last"
        >
          <ChevronsRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
